import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getActionsByUserType } from "../../../config/actions";
import Pagination from "../../common/Pagination";
import { helpRequest } from "./helpApi";
import "../DistributorsAdd/DistributorsAdd.css";
import "../DistributorDashboard/DistributorDashboard.css";
import "./HelpContent.css";

const PAGE_SIZE = 10;

const HelpVideos = () => {
  const userType = localStorage.getItem("user_type");
  const userId = localStorage.getItem("user_id");
  const actions = getActionsByUserType(userType, userId);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const isAdmin = userType === "7";

  const [distributorFilter, setDistributorFilter] = useState("");
  const [distributorsList, setDistributorsList] = useState([]);

  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // video add / edit
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sourceType, setSourceType] = useState("upload");
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [playing, setPlaying] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // category management
  const [catOpen, setCatOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [catBusy, setCatBusy] = useState(false);
  const [catError, setCatError] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([
        helpRequest("/help/videos"),
        helpRequest("/help/video-categories"),
      ]);
      setVideos(v.videos || []);
      setCategories(c.categories || []);
    } catch (err) {
      setVideos([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    if (isAdmin) {
      helpRequest("/admin/distributors")
        .then((data) => setDistributorsList(data.distributors || []))
        .catch(() => setDistributorsList([]));
    }
  }, []);

  // The admin's videos go into admin categories; a distributor's into that
  // distributor's own categories or an admin category (so an admin editing a
  // distributor's video sees that distributor's categories plus the admin ones).
  const categoriesForOwner = (ownerIsAdmin, ownerDistributorId) =>
    categories.filter((c) =>
      ownerIsAdmin
        ? c.is_admin
        : c.is_admin || c.distributor_id === ownerDistributorId
    );
  const newVideoCategories = categoriesForOwner(isAdmin, userId);
  const formCategories = editing
    ? categoriesForOwner(editing.is_admin, editing.distributor_id)
    : newVideoCategories;

  const filteredVideos = videos.filter(
    (v) =>
      (!categoryFilter || String(v.category_id) === categoryFilter) &&
      (!distributorFilter || v.distributor_id === distributorFilter)
  );
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / PAGE_SIZE));
  const paginatedVideos = filteredVideos.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleFilterChange = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleDistributorChange = (value) => {
    setDistributorFilter(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setCategoryFilter("");
    setDistributorFilter("");
    setCurrentPage(1);
  };

  const openForm = (video = null) => {
    setEditing(video);
    setTitle(video ? video.title : "");
    setCategoryId(video ? String(video.category_id) : "");
    setSourceType(video ? "url" : "upload");
    setVideoUrl(video ? video.video_url : "");
    setVideoFile(null);
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!title.trim() || !categoryId) {
      setError("Title and category are required.");
      return;
    }
    if (sourceType === "upload" && !videoFile && !editing) {
      setError("Choose a video file to upload.");
      return;
    }
    if (sourceType === "url" && !videoUrl.trim()) {
      setError("Enter a video URL.");
      return;
    }

    const body = new FormData();
    body.append("title", title.trim());
    body.append("category_id", categoryId);
    if (sourceType === "upload" && videoFile) {
      body.append("form", videoFile);
    } else if (sourceType === "url") {
      body.append("video_url", videoUrl.trim());
    }

    setSaving(true);
    setError("");
    try {
      await helpRequest(editing ? `/help/videos/${editing.id}` : "/help/videos", {
        method: editing ? "PUT" : "POST",
        body,
      });
      setFormOpen(false);
      setEditing(null);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await helpRequest(`/help/videos/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      const remaining = filteredVideos.length - 1;
      setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(remaining / PAGE_SIZE))));
      await loadAll();
    } catch (err) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const runCategoryAction = async (fn) => {
    setCatBusy(true);
    setCatError("");
    try {
      await fn();
      await loadAll();
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatBusy(false);
    }
  };

  const addCategory = () => {
    if (!newCategory.trim()) return;
    runCategoryAction(async () => {
      await helpRequest("/help/video-categories", {
        method: "POST",
        body: { name: newCategory.trim() },
      });
      setNewCategory("");
    });
  };

  const saveRename = (id) => {
    if (!renameValue.trim()) return;
    runCategoryAction(async () => {
      await helpRequest(`/help/video-categories/${id}`, {
        method: "PUT",
        body: { name: renameValue.trim() },
      });
      setRenameId(null);
    });
  };

  const deleteCategory = (id) => {
    runCategoryAction(async () => {
      await helpRequest(`/help/video-categories/${id}`, { method: "DELETE" });
      if (categoryFilter === String(id)) handleFilterChange("");
    });
  };

  return (
    <div className="distributors-add-container">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={actions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />
        <div className="distributors-add-content">
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <h3>Help Videos</h3>
              <p className="head-listing-subtitle">
                Manage the videos displayed in the Help Videos section of the customer support website.
              </p>
            </div>
            <div className="add-btn help-header-actions">
              <button
                onClick={() => {
                  setCatError("");
                  setCatOpen(true);
                }}
              >
                Manage Categories
              </button>
              <button onClick={() => openForm()} disabled={newVideoCategories.length === 0}>
                Add Video
              </button>
            </div>
          </div>

          <div className="filters-section">
            {isAdmin && (
              <div className="filter-group">
                <label>Filter Distributor:</label>
                <select
                  value={distributorFilter}
                  onChange={(e) => handleDistributorChange(e.target.value)}
                >
                  <option value="">All Distributors</option>
                  {distributorsList.map((dist) => (
                    <option key={dist.id} value={dist.distributor_id}>
                      {`${dist.firstname || ""} ${dist.lastname || ""}`.trim() ||
                        dist.business ||
                        `Distributor #${dist.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="filter-group">
              <label>Filter by Category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filters-header">
              <span className="filters-label">Filters</span>
              <button
                className="clear-filters-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="distributors-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="loader-spinner"></div>
                <p>Loading videos...</p>
              </div>
            ) : filteredVideos.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-video"></i>
                <p>
                  {newVideoCategories.length === 0
                    ? "Create a category first, then add videos"
                    : "No videos found"}
                </p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="distributors-table help-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th className="help-col-actions">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedVideos.map((video) => (
                        <tr key={video.id}>
                          <td>{video.title}</td>
                          <td>{video.category_name}</td>
                          <td>
                            <div className="help-actions-cell">
                              <button
                                className="action-btn"
                                title="Play"
                                onClick={() => setPlaying(video)}
                              >
                                <i className="fa fa-play-circle"></i>
                              </button>
                              {video.editable && (
                                <>
                                  <button
                                    className="action-btn"
                                    title="Edit"
                                    onClick={() => openForm(video)}
                                  >
                                    <i className="fa fa-pencil-square-o"></i>
                                  </button>
                                  <button
                                    className="action-btn cancel-btn"
                                    title="Delete"
                                    onClick={() => setDeleteTarget(video)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit video */}
      <Modal isOpen={formOpen} toggle={closeForm} centered>
        <ModalHeader className="modal-header-custom" toggle={closeForm}>
          {editing ? "Edit Video" : "Add Video"}
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="help-form-group">
            <label>Title*</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="help-form-group">
            <label>Category*</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Select category</option>
              {formCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="help-form-group">
            <label>Video*</label>
            <div className="help-source-toggle">
              <label>
                <input
                  type="radio"
                  checked={sourceType === "upload"}
                  onChange={() => setSourceType("upload")}
                />
                Upload file
              </label>
              <label>
                <input
                  type="radio"
                  checked={sourceType === "url"}
                  onChange={() => setSourceType("url")}
                />
                Video URL
              </label>
            </div>
            {sourceType === "upload" ? (
              <>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0] || null)}
                />
                {editing && (
                  <div className="help-hint">
                    Leave empty to keep the current video.
                  </div>
                )}
              </>
            ) : (
              <input
                type="text"
                placeholder="https://..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            )}
          </div>
          {error && <div className="help-error">{error}</div>}
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-secondary" onClick={closeForm} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Player */}
      <Modal isOpen={!!playing} toggle={() => setPlaying(null)} centered size="lg">
        <ModalHeader className="modal-header-custom" toggle={() => setPlaying(null)}>{playing?.title}</ModalHeader>
        <ModalBody className="modal-body-custom">
          {playing && (
            <video
              key={playing.video_url}
              className="help-video-player"
              src={playing.video_url}
              controls
              autoPlay
            />
          )}
        </ModalBody>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader className="modal-header-custom" toggle={() => setDeleteTarget(null)}>Delete Video</ModalHeader>
        <ModalBody className="modal-body-custom">
          Delete "{deleteTarget?.title}"? This cannot be undone.
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button
            className="btn-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Category management */}
      <Modal isOpen={catOpen} toggle={() => setCatOpen(false)} centered>
        <ModalHeader className="modal-header-custom" toggle={() => setCatOpen(false)}>Manage Categories</ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="help-category-add">
            <input
              type="text"
              placeholder="New category name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
            />
            <button
              className="btn-primary-modern"
              onClick={addCategory}
              disabled={catBusy}
            >
              Add
            </button>
          </div>
          {catError && <div className="help-error">{catError}</div>}
          {categories.filter((c) => c.editable).map((c) => (
            <div key={c.id} className="help-category-row">
              {renameId === c.id ? (
                <>
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveRename(c.id)}
                  />
                  <button
                    className="action-btn"
                    title="Save"
                    onClick={() => saveRename(c.id)}
                    disabled={catBusy}
                  >
                    <i className="fa fa-check"></i>
                  </button>
                  <button
                    className="action-btn"
                    title="Cancel"
                    onClick={() => setRenameId(null)}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </>
              ) : (
                <>
                  <span className="help-category-name">{c.name}</span>
                  {c.editable ? (
                    <>
                      <button
                        className="action-btn"
                        title="Rename"
                        onClick={() => {
                          setRenameId(c.id);
                          setRenameValue(c.name);
                        }}
                      >
                        <i className="fa fa-pencil-square-o"></i>
                      </button>
                      <button
                        className="action-btn cancel-btn"
                        title="Delete"
                        onClick={() => deleteCategory(c.id)}
                        disabled={catBusy}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </>
                  ) : (
                    <span className="help-readonly">View only</span>
                  )}
                </>
              )}
            </div>
          ))}
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-secondary" onClick={() => setCatOpen(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default HelpVideos;
